/**
 * Prediction Accuracy Tracker — Sprint 17g-4 Task 5.3
 *
 * Tracks per-category prediction accuracy using a rolling window.
 * Every mission is a prediction; engagement/dismissal scores it.
 * Thresholds (25/50/75/90/100%) earn token rewards when crossed.
 */

import { NS } from '@ownyou/shared-types';

export interface CategoryAccuracy {
  category: string;
  totalPredictions: number;
  hits: number;
  misses: number;
  recentOutcomes: ('hit' | 'miss')[];
  accuracy: number;
  currentThreshold: number;
  thresholdHistory: ThresholdEvent[];
}

export interface ThresholdEvent {
  threshold: number;
  crossedAt: number;
  tokensAwarded: number;
}

export const THRESHOLDS = [25, 50, 75, 90, 100] as const;

export const THRESHOLD_CONFIG: Record<number, { label: string; tokens: number }> = {
  25:  { label: 'Getting to know you',    tokens: 50 },
  50:  { label: 'Learning your patterns', tokens: 100 },
  75:  { label: 'Reading your mind',      tokens: 200 },
  90:  { label: 'Your AI twin',           tokens: 500 },
  100: { label: 'Uncanny',                tokens: 1000 },
};

/** Minimal Store interface */
interface MemoryStore {
  get<T = { value?: unknown }>(namespace: readonly string[], key: string): Promise<T | null>;
  put(namespace: readonly string[], key: string, value: unknown): Promise<void>;
}

export class PredictionTracker {
  private writeLock: Promise<void> = Promise.resolve();

  constructor(private store: MemoryStore, private userId: string) {}

  /**
   * Record a prediction outcome. Returns threshold crossing info if applicable.
   */
  async recordOutcome(
    category: string,
    outcome: 'hit' | 'miss',
  ): Promise<{ thresholdCrossed?: number; tokensAwarded?: number }> {
    // Serialize writes to prevent race conditions (M12)
    let result: { thresholdCrossed?: number; tokensAwarded?: number } = {};
    await (this.writeLock = this.writeLock.then(async () => {
      const accuracy = await this.getCategoryAccuracy(category);

      if (outcome === 'hit') accuracy.hits++;
      else accuracy.misses++;
      accuracy.totalPredictions = accuracy.hits + accuracy.misses;

      // Windowed accuracy (last 20 predictions)
      accuracy.recentOutcomes = accuracy.recentOutcomes ?? [];
      accuracy.recentOutcomes.push(outcome);
      if (accuracy.recentOutcomes.length > 20) {
        accuracy.recentOutcomes = accuracy.recentOutcomes.slice(-20);
      }
      const recentHits = accuracy.recentOutcomes.filter(o => o === 'hit').length;
      accuracy.accuracy = accuracy.recentOutcomes.length > 0
        ? recentHits / accuracy.recentOutcomes.length
        : 0;

      // Check for threshold crossing
      const accuracyPct = accuracy.accuracy * 100;
      const nextThreshold = THRESHOLDS.find(t => t > accuracy.currentThreshold && accuracyPct >= t);

      if (nextThreshold) {
        const config = THRESHOLD_CONFIG[nextThreshold];
        accuracy.currentThreshold = nextThreshold;
        accuracy.thresholdHistory.push({
          threshold: nextThreshold,
          crossedAt: Date.now(),
          tokensAwarded: config.tokens,
        });
        result = { thresholdCrossed: nextThreshold, tokensAwarded: config.tokens };
      }

      await this.saveCategoryAccuracy(category, accuracy);
    }));
    return result;
  }

  async getAllAccuracies(): Promise<CategoryAccuracy[]> {
    const stored = await this.store.get(NS.predictionAccuracy(this.userId), 'all');
    return (stored?.value as { categories?: CategoryAccuracy[] })?.categories ?? [];
  }

  private async getCategoryAccuracy(category: string): Promise<CategoryAccuracy> {
    const all = await this.getAllAccuracies();
    return all.find(c => c.category === category) ?? {
      category,
      totalPredictions: 0,
      hits: 0,
      misses: 0,
      recentOutcomes: [],
      accuracy: 0,
      currentThreshold: 0,
      thresholdHistory: [],
    };
  }

  private async saveCategoryAccuracy(category: string, accuracy: CategoryAccuracy): Promise<void> {
    const all = await this.getAllAccuracies();
    const idx = all.findIndex(c => c.category === category);
    if (idx >= 0) all[idx] = accuracy;
    else all.push(accuracy);
    await this.store.put(NS.predictionAccuracy(this.userId), 'all', { categories: all });
  }
}
